<template>
  <v-row>
    <v-col cols="12">
      <v-text-field
        filled
        dense
        rounded
        :suffix="$vuetify.breakpoint.smAndUp ? $t('search-suffix'): ''"
        v-model="options.search"
        prepend-inner-icon="mdi-magnify"
        :label="$t('actions.search-three-dots')"
        single-line
        hide-details
      ></v-text-field>
    </v-col>
    <v-col cols="12">
      <v-data-table
        :mobile-breakpoint="0"
        class="table-checks pi-4"
        :headers="tableHeaders"
        :items="rows.all"
        :options.sync="options"
        v-model="rows.selected"
        :show-select="$vuetify.breakpoint.mdAndUp"
        :server-items-length="rows.count"
        :footer-props="{ 'items-per-page-options': [20, 50, 100] }"
        :single-expand="false"
        :expanded="expanded"
      >
        <template v-slot:expanded-item="{ headers, item }">
          <td :colspan="headers.length"
              style="height: 38px; padding-bottom: 12px !important; text-align: center; border-bottom: thin solid rgba(0, 0, 0, 0.12);">
            <check-positions
              @clicked="showPositionDetails"
              :check="item"
              :datacenters="reports.find(item.id)">
            </check-positions>
          </td>
        </template>
        <template v-slot:item.created_at="{ item }">
          {{ $moment(item.created_at).format('L LT') }}
        </template>
        <template v-slot:item.keyword.term="{ item }">
          <v-tooltip bottom v-if="$vuetify.breakpoint.xs">
            <template v-slot:activator="{ on, attrs }">
              <span v-bind="attrs" v-on="on">{{ item.keyword.term }}</span>
            </template>
            <span>{{ item.keyword.term }}</span>
          </v-tooltip>
          <span v-else>{{ item.keyword.term }}</span>
        </template>
        <template v-slot:item.domain="{ item }">
          <v-tooltip bottom v-if="$vuetify.breakpoint.xs">
            <template v-slot:activator="{ on, attrs }">
              <span v-bind="attrs" v-on="on">{{ item.domain }}</span>
            </template>
            <span>{{ item.domain }}</span>
          </v-tooltip>
          <span v-else>{{ item.domain }}</span>
        </template>
        <template v-slot:item.geo_lang="{ item }">
          <geo-lang-name :country="item.keyword.country" :language="item.keyword.language"></geo-lang-name>
        </template>
        <template v-slot:item.average="{ item }">
          <check-average-positions :datacenters="reports.find(item.id)"></check-average-positions>
        </template>
        <template v-slot:item.results="{ item }">
          <check-positions
            @clicked="showPositionDetails"
            :check="item"
            :datacenters="reports.find(item.id)">
          </check-positions>
        </template>
      </v-data-table>
    </v-col>
  </v-row>
</template>

<script>
import CheckPositions from './Common/CheckPositions'
import CheckAveragePositions from './Common/CheckAveragePositions'
import GeoLangName from '@/views/Common/Components/GeoLang/GeoLangName'
import { debounce } from 'lodash'

export default {
  name: 'PageTable',
  components: { GeoLangName, CheckAveragePositions, CheckPositions },
  data () {
    return {
      loading: false,
      headers: [
        { visible: 'md', text: this.$t('headers.created-at'), value: 'created_at' },
        { visible: 'xs', text: this.$t('headers.keyword-term'), value: 'keyword.term' },
        { visible: 'xs', text: this.$t('headers.domain'), value: 'domain' },
        { visible: 'md', text: this.$t('headers.geo-lang'), value: 'geo_lang', sortable: false },
        { visible: 'xs', text: this.$t('headers.average'), value: 'average', sortable: false },
        { visible: 'md', text: this.$t('headers.results'), value: 'results', sortable: false }
      ],
      options: {
        page: 1,
        itemsPerPage: 20,
        sortBy: [],
        sortDesc: [],
        groupBy: [],
        groupDesc: [],
        mustSort: false,
        multiSort: false,
        search: ''
      },
      rows: {
        all: [],
        selected: [],
        count: null
      }
    }
  },
  watch: {
    options: {
      deep: true,
      handler () {
        this.fetch()
      }
    },
    computedSelectedRowsIds (value) {
      this.$emit('select', value)
    }
  },
  computed: {
    reports () {
      return this.$services.datacenterCheck.reports
    },
    computedSelectedRowsIds () {
      return this.rows.selected.map(selected => {
        return selected.id
      })
    },
    tableHeaders () {
      let weights = { 'xs': 1, 'sm': 2, 'md': 3, 'lg': 4, 'xl': 5 }
      return this.headers.filter(header => weights[header.visible] <= weights[this.$vuetify.breakpoint.name])
    },
    expanded () {
      return this.$vuetify.breakpoint.smAndDown ? this.rows.all : []
    }
  },
  methods: {
    onFormSubmit () {
      this.fetch()
    },
    async refresh () {
      this.rows.selected = []
      await this.fetch()
    },
    fetch: debounce(async function (silent) {
      const response = await this.$api.get('checks/datatable', {
        datatable: true,
        page: this.options.page,
        items_per_page: this.options.itemsPerPage,
        sort_by: this.options.sortBy,
        sort_desc: this.options.sortDesc,
        search: this.options.search,
        with_keyword: true
      }, {
        showLoading: !silent,
        notifyErrors: false,
        throwErrors: false
      })

      this.rows.all = response.data
      this.rows.count = response.meta.total

      this.$emit('fetched', response)
    }, 200),
    showPositionDetails ({ check, datacenter }) {
      this.$ui.openDialog('check_serp', {
        check,
        datacenter
      })
    },
  },
  created () {
    this.$bus.on('datacenter_check/require_refresh', this.refresh)

    this.reports.watch(() => {
      return Object.fromEntries(this.rows.all.map(check => [check.id, this.$models.CarrierObjects.checkReport(check)]))
    })

    if(this.$account.isVerified) {
      this.fetch()
    } else {
      this.rows.all = []
      this.rows.count = 0
    }
  },
  destroyed () {
    this.$bus.off('datacenter_check/require_refresh', this.refresh)
  }
}
</script>

<style>
/* Removes the shadow for expand */
.table-checks > .v-data-table__wrapper tbody tr.v-data-table__expanded__content {
  box-shadow: none !important;
  line-height: 16px !important;
}

/* No bottom border and fixed height for expand on mobile */
@media screen and (max-width: 960px) {
  .table-checks > .v-data-table__wrapper > table > tbody > tr:not(:last-child) > td:not(.v-data-table__mobile-row), .theme--light.v-data-table > .v-data-table__wrapper > table > tbody > tr:not(:last-child) > th:not(.v-data-table__mobile-row) {
    border-bottom: none !important;
  }

  .table-checks > .v-data-table__wrapper > table > tbody > tr:hover:not(.v-data-table__expanded__content):not(.v-data-table__empty-wrapper) {
    background: none !important;
    height: 24px !important;
  }
}
</style>

<i18n>
{
	"en": {
		"headers": {
			"average": "Pos.",
			"created-at": "Dated",
			"domain": "Website",
			"geo-lang": "Geoloc & Lang",
			"keyword-term": "Keyword",
			"results": "Positions (1st result)"
		},
		"search-suffix": "(domain name, keyword ...)"
	},
	"es": {
		"headers": {
			"average": "Pos.",
			"created-at": "Fecha",
			"domain": "Sitio Web",
			"geo-lang": "Geoloc y lengua",
			"keyword-term": "Palabra clave",
			"results": "Posiciones (1er resultado)"
		},
		"search-suffix": "(nombre de dominio, palabra clave ...)"
	},
	"fr": {
		"headers": {
			"average": "Pos.",
			"created-at": "Date",
			"domain": "Site web",
			"geo-lang": "Géoloc & Langue",
			"keyword-term": "Mot-clé",
			"results": "Positions (1er resultat)"
		},
		"search-suffix": "(nom de domaine, mot clé ...)"
	},
	"it": {
		"headers": {
			"average": "Pos.",
			"created-at": "Data",
			"domain": "Sito web",
			"geo-lang": "Geoloc & Lingua",
			"keyword-term": "Parola chiave",
			"results": "Posizioni (1° risultato)"
		},
		"search-suffix": "(nome di dominio, parola chiave ...)"
	},
	"pt": {
		"headers": {
			"average": "Pos.",
			"created-at": "Data",
			"domain": "Website",
			"geo-lang": "Geoloc & Idioma",
			"keyword-term": "Palavra-chave",
			"results": "Posições (1.º resultado)"
		},
		"search-suffix": "(nome de domínio, palavra-chave ...)"
	},
	"pl": {
		"headers": {
			"average": "Poz.",
			"created-at": "Data",
			"domain": "Strona internetowa",
			"geo-lang": "Geolok. i Język",
			"keyword-term": "Słowo kluczowe",
			"results": "Pozycje (1. wynik)"
		},
		"search-suffix": "(nazwa domeny, słowo kluczowe ...)"
	}
}
</i18n>